import { Injectable, signal } from '@angular/core';
import { AuthService } from '../auth';
import { ModalService } from '../../shared/services';
import { ServiceBase } from '../../shared/models/bases';
import { Business } from '../../shared/models/business';
import { BusinessStore } from '../../stores/business';
import { API_ENDPOINTS } from '../../shared/common/apiConstants';
import { environment } from '../../../environments/environment';
import { HttpContext, HttpParams } from '@angular/common/http';
import {
  Client,
  GetRecordOptions,
  PagedRecord,
  Professional,
  Service,
} from '../../shared/interfaces';
import { REQUIRE_AUTH } from '../../shared/interceptors/auth-context';
import { map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BusinessService extends ServiceBase<Business> {
  needsBusinessInfo = signal<boolean>(false);
  localBusinessIsActive: boolean | null = false;
  apiBaseUrl = environment.apiUrl;

  constructor(
    private authService: AuthService,
    private modalService: ModalService,
    businessStore: BusinessStore
  ) {
    super(API_ENDPOINTS.businesses.business, businessStore);
  }

  checkAndPromptBusinessInfo(): void {
    // Skip business info modal for system administrators
    if (this.authService.isSystemAdmin()) {
      this.needsBusinessInfo.set(false);
      return;
    }

    const user = this.authService.getUserInfo();
    const tokenActive = !!(
      user?.business_is_active === true || user?.business_is_active === 'true'
    );
    const localActive = !!this.authService.getBusinessIsActive();
    const isActive = tokenActive || localActive;
    this.needsBusinessInfo.set(!isActive);

    if (!isActive) {
      this.modalService.open('business-info');
    }
  }

  onBusinessSaved(newBusinessName: string): void {
    this.authService.markBusinessActive(newBusinessName);
    this.needsBusinessInfo.set(false);
    this.modalService.close();
  }

  getRecordsByBusinessId<T>(
    businessId: string,
    opts: GetRecordOptions = {},
    endpoint: string
  ): Observable<PagedRecord<T>> {
    const { page = 1, pageSize = 20, searchTerm, sort, order, isActive } = opts;

    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));

    if (searchTerm && searchTerm.trim())
      params = params.set('searchTerm', searchTerm.trim());
    if (sort) params = params.set('sort', sort);
    if (order) params = params.set('order', order);
    if (typeof isActive === 'boolean')
      params = params.set('isActive', String(isActive));

    return this.http.get<PagedRecord<T>>(
      `${this.baseUrl}/api/v1/businesses/${businessId}/${endpoint}`,
      {
        params,
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  getRecordByBusinessId<T>(
    businessId: string,
    entityId: string,
    endpoint: string
  ): Observable<T> {
    return this.http.get<T>(
      `${this.baseUrl}/api/v1/businesses/${businessId}/${endpoint}/${entityId}`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  createRecordByBusinessId<T>(businessId: string, model: T, endpoint: string) {
    return this.http.post(
      `${this.baseUrl}/api/v1/businesses/${businessId}/${endpoint}`,
      model,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  updateRecordByBusinessId<T>(
    businessId: string,
    entityId: string,
    model: T,
    endpoint: string
  ) {
    return this.http.put(
      `${this.baseUrl}/api/v1/businesses/${businessId}/${endpoint}/${entityId}`,
      model,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  deleteRecordByBusinessId<T>(
    businessId: string,
    entityId: string,
    endpoint: string
  ) {
    return this.http.delete(
      `${this.baseUrl}/api/v1/businesses/${businessId}/${endpoint}/${entityId}`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  toggleProfessionalStatus(
    businessId: string,
    professionalId: string,
    isActive: boolean
  ): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}/api/v1/businesses/${businessId}/professionals/${professionalId}/status`,
      { isActive },
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  searchEntityByBusinessId(
    businessId: string,
    endpoint: 'clients',
    query?: string,
    page?: number,
    pageSize?: number
  ): Observable<Client[]>;

  searchEntityByBusinessId(
    businessId: string,
    endpoint: 'professionals',
    query?: string,
    page?: number,
    pageSize?: number
  ): Observable<Professional[]>;

  searchEntityByBusinessId(
    businessId: string,
    endpoint: 'services',
    query?: string,
    page?: number,
    pageSize?: number
  ): Observable<Service[]>;

  searchEntityByBusinessId(
    businessId: string,
    endpoint: 'clients' | 'services' | 'professionals',
    query: string,
    page = 1,
    pageSize = 20
  ): Observable<Client[] | Service[] | Professional[]> {
    const params = new HttpParams()
      .set('searchTerm', query)
      .set('page', page)
      .set('pageSize', pageSize);

    return this.http
      .get<any>(`${this.baseUrl}/api/v1/businesses/${businessId}/${endpoint}`, {
        params,
        context: new HttpContext().set(REQUIRE_AUTH, true),
      })
      .pipe(
        map((res) => {
          if (endpoint === 'clients') {
            const list: Client[] = (res?.clients ?? []).map((d: Client) =>
              this.toClient(d)
            );
            return list;
          } else if (endpoint === 'professionals') {
            const list: Professional[] = (res?.professionals ?? []).map((d: any) =>
              this.toProfessional(d)
            );
            return list;
          } else {
            const list: Service[] = (res?.services ?? []).map((d: Service) =>
              this.toService(d)
            );
            return list;
          }
        })
      );
  }

  getBusinessById(businessId: string) {
    return this.http.get(`/api/v1/businesses/${businessId}`);
  }

  updateBusiness(businessId: string, dto: any) {
    return this.http.put(`/api/v1/businesses/${businessId}`, dto);
  }

  updateBusinessProfile(
    businessId: string,
    payload: {
      name?: string | null;
      description?: string | null;
      logoFile?: File | null;
      logoUrl?: string | null;
      primaryColor?: string | null;
      secondaryColor?: string | null;
      address?: string | null;
      phone?: string | null;
      whatsApp?: string | null;
    }
  ) {
    const formData = new FormData();
    if (payload.name !== undefined && payload.name !== null) {
      formData.append('Name', payload.name);
    }
    if (payload.description !== undefined) {
      formData.append('Description', payload.description ?? '');
    }
    if (payload.logoUrl) {
      formData.append('LogoUrl', payload.logoUrl);
    }
    if (payload.logoFile) {
      formData.append('LogoFile', payload.logoFile);
    }
    if (payload.primaryColor !== undefined) {
      formData.append('PrimaryColor', payload.primaryColor ?? '');
    }
    if (payload.secondaryColor !== undefined) {
      formData.append('SecondaryColor', payload.secondaryColor ?? '');
    }
    if (payload.address !== undefined) {
      formData.append('Address', payload.address ?? '');
    }
    if (payload.phone !== undefined) {
      formData.append('Phone', payload.phone ?? '');
    }
    if (payload.whatsApp !== undefined) {
      formData.append('WhatsApp', payload.whatsApp ?? '');
    }

    return this.http.put<{ LogoUrl?: string; Message?: string }>(
      `${this.baseUrl}/api/v1/businesses/${businessId}`,
      formData,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  /**
   * Actualiza el slug público del negocio
   */
  updateBusinessSlug(businessId: string, publicSlug: string) {
    return this.http.put<{ Message: string }>(
      `${this.baseUrl}/api/v1/businesses/${businessId}/public-slug`,
      { publicSlug },
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  /**
   * Valida si un slug está disponible
   */
  validateSlug(slug: string, businessId?: string): Observable<{
    isValid: boolean;
    isAvailable: boolean;
    message?: string;
  }> {
    let params = new HttpParams().set('slug', slug);
    if (businessId) {
      params = params.set('businessId', businessId);
    }
    return this.http.get<{
      isValid: boolean;
      isAvailable: boolean;
      message?: string;
    }>(`${this.baseUrl}/api/v1/businesses/validate-slug`, {
      params,
      context: new HttpContext().set(REQUIRE_AUTH, true),
    });
  }

  // Upload de logo opcional (si tu API lo soporta)
  uploadBusinessLogo(businessId: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    return this.http.post(
      `${this.baseUrl}/api/v1/businesses/${businessId}/logo`,
      fd,
      { context: new HttpContext().set(REQUIRE_AUTH, true) }
    );
  }

  // Upload de imagen de servicio
  uploadServiceImage(businessId: string, serviceId: string, file: File) {
    const fd = new FormData();
    fd.append('imageFile', file);
    return this.http.post<{ ImageUrl: string; Message: string }>(
      `${this.baseUrl}/api/v1/businesses/${businessId}/services/${serviceId}/upload-image`,
      fd,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  // Upload de avatar de profesional
  uploadProfessionalAvatar(businessId: string, professionalId: string, file: File) {
    const fd = new FormData();
    fd.append('avatarFile', file);
    return this.http.post<{ AvatarUrl: string; Message: string }>(
      `${this.baseUrl}/api/v1/businesses/${businessId}/professionals/${professionalId}/upload-avatar`,
      fd,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  private toClient = (dto: Client): Client => ({
    userId: dto.userId,
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email,
    phone: dto.phone,
    isActive: dto.isActive,
    countryId: (dto as any).countryId,
    countryName: dto.countryName,
    createdAt: dto.createdAt,
    //avatarUrl: dto.avatarUrl ?? null,
  });

  private toProfessional = (dto: any): Professional => ({
    userId: dto.userId,
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email,
    phone: dto.phone,
    isActive: dto.isActive,
    countryId: dto.countryId,
    countryName: dto.countryName,
    createdAt: dto.createdAt,
    color: dto.color ?? null,
    avatarUrl: dto.avatarUrl ?? null,
  });

  private toService(dto: Service): Service {
    return {
      serviceId: dto.serviceId,
      name: dto.name,
      description: dto.description ?? '',
      price: dto.price,
      durationMinutes: dto.durationMinutes,
      createdAt: dto.createdAt,
      createdByName: dto.createdByName,
      hasSessions: dto.hasSessions,
      sessionsCount: dto.sessionsCount,
      sessionIntervalDays: dto.sessionIntervalDays,
      imageUrl: (dto as any).imageUrl ?? null,
    };
  }
}
